import { Routes, Route } from 'react-router-dom';
import MainLayout from '@/shared/components/layout/MainLayout';
import { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { Spin, ConfigProvider, App as AntdApp } from 'antd';
import viVN from 'antd/locale/vi_VN';
import { FlyAnimationProvider, ScrollToTop, ErrorBoundary } from '@/shared/components/common';
import { ProtectedRoute } from '@/shared/components/auth';

// Client Pages (Tailwind + shadcn)
const HomePage = lazy(() => import('@/features/home/pages/HomePage'));
const ProductsPage = lazy(() => import('@/features/products/pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('@/features/products/pages/ProductDetailPage'));
const CartPage = lazy(() => import('@/features/cart/pages/CartPage'));
const CheckoutPage = lazy(() => import('@/features/checkout/pages/CheckoutPage'));
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const DashboardLayout = lazy(() => import('@/features/profile/layouts/DashboardLayout'));
const ProfileInfoPage = lazy(() => import('@/features/profile/pages/ProfileInfoPage'));
const ProfileOrdersPage = lazy(() => import('@/features/profile/pages/ProfileOrdersPage'));
const AddressesPage = lazy(() => import('@/features/profile/pages/AddressesPage'));
const PasswordPage = lazy(() => import('@/features/profile/pages/PasswordPage'));
const OrdersPage = lazy(() => import('@/features/orders/pages/OrdersPage'));
const OrderDetailPage = lazy(() => import('@/features/orders/pages/OrderDetailPage'));
const ThankYouPage = lazy(() => import('@/features/checkout/pages/ThankYouPage'));
const ContactPage = lazy(() => import('@/features/pages/pages/ContactPage'));
const NotFoundPage = lazy(() => import('@/features/pages/pages/NotFoundPage'));

// Admin Pages (Antd)
const AdminLayout = lazy(() => import('@/features/admin/layouts/AdminLayout'));
const DashboardPage = lazy(() => import('@/features/admin/pages/DashboardPage'));
const AdminProductsPage = lazy(() => import('@/features/admin/pages/AdminProductsPage'));
const AdminProductFormPage = lazy(() => import('@/features/admin/pages/AdminProductFormPage'));
const AdminCategoriesPage = lazy(() => import('@/features/admin/pages/AdminCategoriesPage'));
const AdminOrdersPage = lazy(() => import('@/features/admin/pages/AdminOrdersPage'));
const AdminUsersPage = lazy(() => import('@/features/admin/pages/AdminUsersPage'));
const QueueMetricsPage = lazy(() => import('@/features/admin/pages/QueueMetricsPage'));
const BannerManagerPage = lazy(() => import('@/features/admin/pages/BannerManagerPage'));
const PageBuilderPage = lazy(() => import('@/features/admin/pages/PageBuilderPage'));
const PagesListPage = lazy(() => import('@/features/admin/pages/PagesListPage'));
const AdminCouponsPage = lazy(() => import('@/features/admin/pages/AdminCouponsPage'));
const AdminReviewsPage = lazy(() => import('@/features/admin/pages/AdminReviewsPage'));
const AdminSettingsPage = lazy(() => import('@/features/admin/pages/AdminSettingsPage'));
const AdminProfilePage = lazy(() => import('@/features/admin/pages/AdminProfilePage'));
const DynamicPage = lazy(() => import('@/features/pages/pages/DynamicPage'));

// Client loading spinner (shadcn style)
const ClientLoading = () => (
    <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
);

// Admin loading spinner (Antd style)
const AdminLoading = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
    </div>
);

// Antd theme for admin - Green theme
const antdTheme = {
    token: {
        colorPrimary: '#519140',
        colorLink: '#519140',
        colorSuccess: '#10b981',
        colorWarning: '#f59e0b',
        colorError: '#ef4444',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: 8,
    },
};

function App() {
    return (
        <FlyAnimationProvider>
            <ScrollToTop />
            <Routes>
                {/* Client Routes - Tailwind + shadcn/ui */}
                <Route
                    path="/"
                    element={
                        <Suspense fallback={<ClientLoading />}>
                            <MainLayout />
                        </Suspense>
                    }
                >
                    <Route index element={<Suspense fallback={<ClientLoading />}><HomePage /></Suspense>} />
                    <Route path="products" element={<Suspense fallback={<ClientLoading />}><ProductsPage /></Suspense>} />
                    <Route path="products/:slug" element={<Suspense fallback={<ClientLoading />}><ProductDetailPage /></Suspense>} />
                    <Route path="cart" element={<Suspense fallback={<ClientLoading />}><CartPage /></Suspense>} />
                    <Route path="checkout" element={<Suspense fallback={<ClientLoading />}><CheckoutPage /></Suspense>} />
                    <Route path="profile" element={<Suspense fallback={<ClientLoading />}><DashboardLayout /></Suspense>}>
                        <Route index element={<Suspense fallback={<ClientLoading />}><ProfileInfoPage /></Suspense>} />
                        <Route path="orders" element={<Suspense fallback={<ClientLoading />}><ProfileOrdersPage /></Suspense>} />
                        <Route path="addresses" element={<Suspense fallback={<ClientLoading />}><AddressesPage /></Suspense>} />
                        <Route path="password" element={<Suspense fallback={<ClientLoading />}><PasswordPage /></Suspense>} />
                    </Route>
                    <Route path="orders" element={<Suspense fallback={<ClientLoading />}><OrdersPage /></Suspense>} />
                    <Route path="orders/:id" element={<Suspense fallback={<ClientLoading />}><OrderDetailPage /></Suspense>} />
                    <Route path="order-success/:orderId" element={<Suspense fallback={<ClientLoading />}><ThankYouPage /></Suspense>} />
                    <Route path="pages/:slug" element={<Suspense fallback={<ClientLoading />}><DynamicPage /></Suspense>} />
                    <Route path="contact" element={<Suspense fallback={<ClientLoading />}><ContactPage /></Suspense>} />
                </Route>

                {/* Auth Routes - Tailwind + shadcn/ui */}
                <Route path="/login" element={<Suspense fallback={<ClientLoading />}><LoginPage /></Suspense>} />
                <Route path="/register" element={<Suspense fallback={<ClientLoading />}><RegisterPage /></Suspense>} />
                <Route path="/forgot-password" element={<Suspense fallback={<ClientLoading />}><ForgotPasswordPage /></Suspense>} />
                <Route path="/reset-password" element={<Suspense fallback={<ClientLoading />}><ResetPasswordPage /></Suspense>} />

                {/* 404 Catch-all route - must be last, with MainLayout */}
                <Route
                    path="*"
                    element={
                        <Suspense fallback={<ClientLoading />}>
                            <MainLayout />
                        </Suspense>
                    }
                >
                    <Route index element={<Suspense fallback={<ClientLoading />}><NotFoundPage /></Suspense>} />
                </Route>

                {/* Admin Routes - Pure Antd - Protected with Admin Role */}
                <Route
                    path="/admin"
                    element={
                        <ProtectedRoute requireAdmin={true}>
                            <ErrorBoundary>
                                <ConfigProvider locale={viVN} theme={antdTheme}>
                                    <AntdApp>
                                        <Suspense fallback={<AdminLoading />}>
                                            <AdminLayout />
                                        </Suspense>
                                    </AntdApp>
                                </ConfigProvider>
                            </ErrorBoundary>
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Suspense fallback={<AdminLoading />}><DashboardPage /></Suspense>} />
                    <Route path="products" element={<Suspense fallback={<AdminLoading />}><AdminProductsPage /></Suspense>} />
                    <Route path="products/new" element={<Suspense fallback={<AdminLoading />}><AdminProductFormPage /></Suspense>} />
                    <Route path="products/:id/edit" element={<Suspense fallback={<AdminLoading />}><AdminProductFormPage /></Suspense>} />
                    <Route path="categories" element={<Suspense fallback={<AdminLoading />}><AdminCategoriesPage /></Suspense>} />
                    <Route path="orders" element={<Suspense fallback={<AdminLoading />}><AdminOrdersPage /></Suspense>} />
                    <Route path="users" element={<Suspense fallback={<AdminLoading />}><AdminUsersPage /></Suspense>} />
                    <Route path="banners" element={<Suspense fallback={<AdminLoading />}><BannerManagerPage /></Suspense>} />
                    <Route path="pages" element={<Suspense fallback={<AdminLoading />}><PagesListPage /></Suspense>} />
                    <Route path="pages/new" element={<Suspense fallback={<AdminLoading />}><PageBuilderPage /></Suspense>} />
                    <Route path="pages/:id" element={<Suspense fallback={<AdminLoading />}><PageBuilderPage /></Suspense>} />
                    <Route path="coupons" element={<Suspense fallback={<AdminLoading />}><AdminCouponsPage /></Suspense>} />
                    <Route path="reviews" element={<Suspense fallback={<AdminLoading />}><AdminReviewsPage /></Suspense>} />
                    <Route path="settings" element={<Suspense fallback={<AdminLoading />}><AdminSettingsPage /></Suspense>} />
                    <Route path="profile" element={<Suspense fallback={<AdminLoading />}><AdminProfilePage /></Suspense>} />
                    <Route path="queue-metrics" element={<Suspense fallback={<AdminLoading />}><QueueMetricsPage /></Suspense>} />
                </Route>
            </Routes>
        </FlyAnimationProvider>
    );
}

export default App;
